import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { withStyles, Card, Table, usePagination } from 'vopay';
import styles from './ClientAccountsPage.module.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { Title, Button, Paginator, TableFiltersContainer } from 'UI';
import { FormValues, useFilterFormConfig } from './useFilterFormConfig';
import useTableConfig from './useTableConfig';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

const PAGE_SIZE = 10;

const ClientAccountsPage: FC = () => {
  const history = useHistory();
  const { pending, pagination, clientAccounts, fetchClientAccounts } = useClientAccounts();
  const tableConfig = useTableConfig(clientAccounts);
  const [, setPending] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const overflowRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atScrollStart, setAtScrollStart] = useState(false);
  const [atScrollEnd, setAtScrollEnd] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  useEffect(() => {
    const ref = overflowRef.current;

    if (!ref) return;

    const obs = new MutationObserver(() => {
      setHasOverflow(ref.scrollWidth > ref.clientWidth);
      handleScroll();
    });

    obs.observe(ref, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const handleScroll = () => {
      setAtScrollStart(ref.scrollLeft < 10);
      setAtScrollEnd(Math.abs(ref.clientWidth + ref.scrollLeft - ref.scrollWidth) < 10);
    };

    ref.addEventListener('scroll', handleScroll);

    return () => {
      obs.disconnect();
      ref.removeEventListener('scroll', handleScroll);
    };
  }, [overflowRef.current]);

  useEffect(() => {
    setPending(pending);
  }, [pending]);

  const { pageCount, pageIndex, setPageIndex } = usePagination(pagination.TotalRecords, {
    pageSize: PAGE_SIZE,
  });

  const { query, setQuery, filtersApplied } = useQuery();

  const { renderFormElements, ctx } = useFilterFormConfig({
    query,
    onSubmit: (values) => {
      setQuery(values);
    },
  });

  useEffect(() => {
    setPageIndex(0);
    refresh(0);
  }, [query]);

  async function refresh(offset: number) {
    const balanceRange = query.balance?.split('|') || [];

    try {
      await fetchClientAccounts({
        Offset: offset,
        Limit: PAGE_SIZE,
        SearchText: query.keyword,
        IsBusiness: query.clientType ? query.clientType === 'business' : undefined,
        Label: query.label,
        MinBalance: balanceRange[0] || undefined,
        MaxBalance: balanceRange[1] || undefined,
        AccountStatus: query.accountStatus ? query.accountStatus === 'active' : undefined,
      });
    } catch (err) {
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Error' }),
        t('errorContent', { defaultValue: 'An error occurred while fetching client accounts' }),
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );

      console.error(err);
    }
  }

  return (
    <div className={cn('clientAccountsPage')}>
      <header className={cn('pageHeader')}>
        <Title>{t('pageTitle', { defaultValue: 'Client Accounts' })}</Title>
        <Button colorScheme="primary" onClick={() => history.push('/client-accounts/create', { showBackButton: true })}>
          {t('addClientAccount', { defaultValue: 'Add Client Account' })}
        </Button>
      </header>

      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <form className={cn('filtersInner')} onSubmit={ctx.handleSubmit}>
              {renderFormElements()}

              <Button type="submit" colorScheme="primary" size="small">
                {tGlobal('Buttons.search', { defaultValue: 'Search' })}
              </Button>
            </form>
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <div
          className={cn(
            'tableContainerWithFade',
            hasOverflow && 'hasOverflow',
            atScrollStart && 'atScrollStart',
            atScrollEnd && 'atScrollEnd',
          )}
        >
          <div ref={overflowRef} className={cn('tableOverflowContainer')}>
            <Table version="2" {...tableConfig} />
          </div>
        </div>

        {pageCount > 1 && (
          <div className={cn('paginator')}>
            <Paginator
              pageCount={pageCount}
              onPageChange={(e) => {
                const newOffset = setPageIndex(e.selected);
                refresh(newOffset);
              }}
              pageOffset={pageIndex}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientAccountsPage;

function useQuery() {
  const { search } = useLocation();
  const history = useHistory();

  const query = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(search)) as Omit<FormValues, 'balance'> & {
      balance: string;
    };
  }, [search]);

  const filtersApplied = useMemo(() => Object.keys(query).length > 0, [query]);

  function setQuery(data: FormValues) {
    const newParams = new URLSearchParams(
      Object.entries(data).reduce((acc, [key, val]) => {
        if (!val) return acc;

        return {
          ...acc,
          [key]: val,
        };
      }, {}),
    );

    history.replace({ pathname: location.pathname, search: newParams.toString() });
  }

  return {
    query,
    setQuery,
    filtersApplied,
  };
}
